import { FC, ReactElement } from 'react';
import { EditTextarea } from 'react-edit-text';
import { useIntl } from 'react-intl';

import { useApp } from '@lib/core/service/hooks';
import StyledTooltip from '@lib/tools/devtools/components/feedback/Tooltip';
import { useDevtoolsLocale } from '@lib/tools/locale/hooks/devtoolsLocale';
import { LocaleUtils } from '@lib/tools/locale/utils';
import { languages } from '@lib/tools/locale/utils/consts';
import CustomMDReactComponent from '@lib/tools/locale/views/CustomMDReactComponent';
import { InsertIntlJSXPart } from '@lib/tools/locale/views/InsertIntlJSXPart';

// eslint-disable-next-line no-restricted-imports
import './locale_fragment.scss';
import 'react-edit-text/dist/index.css';

export type ILocaleType = { readonly id: string; readonly defaultMessage: string };
export type ILocaleText = ILocaleType | string;
export type TInsertIntlJSXPartOptions = {
  linkId: string;
  linkRender: (text: string) => ReactElement;
  className?: string;
  textId: string;
};

export interface LocaleFragmentProps {
  message: ILocaleText;
  options?: Record<string, string>;
  variables?: Record<string, ILocaleType>;
  index?: Record<string, string>;
  insertIntlJSXPartOptions?: TInsertIntlJSXPartOptions;
  transformText?: 'default' | 'none' | 'custom-pmi' | 'lowercase' | 'markdown';
}

/**
 * * This component is used for any static text. It injectsIntl and sources translations from backend or local files.
 * ! Pass productCategory into the options payload
 * Can handle both JSON terms and plain strings.
 * Supports markdown.
 */
export const LocaleFragment: FC<LocaleFragmentProps> = ({
  message,
  insertIntlJSXPartOptions,
  options = {},
  variables,
  index = {},
  transformText = 'default',
}) => {
  const { formatMessage } = useIntl();
  const { detectIsDraftTermsUpdated } = useDevtoolsLocale();
  const { isDevToolsEnabled, isLocaleEditMode, locale } = useApp();
  const renderContent = (text: string) => <CustomMDReactComponent text={text} transformText={transformText} />;

  if (typeof message === 'string') {
    return isDevToolsEnabled ? (
      <span data-testid={message.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}>{renderContent(message)}</span>
    ) : (
      renderContent(message)
    );
  }

  const getText = () => {
    const mergedOptions = { ...index, ...options };
    if (variables) {
      Object.entries(variables).forEach(([key, value]) => {
        mergedOptions[key] = formatMessage(value);
      });
    }
    return formatMessage(message, mergedOptions).replaceAll('||', '   \n');
  };

  const text = getText();
  const reactMarkdownText = renderContent(text);

  if (isDevToolsEnabled && isLocaleEditMode && locale !== languages.ENGLISH) {
    return (
      <StyledTooltip arrow title={message.id}>
        <div
          className="locale-fragment-wrapper"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <EditTextarea
            className="locale-fragment"
            defaultValue={text}
            id={message.id}
            inputClassName="locale-fragment-input"
            onSave={async data => {
              await LocaleUtils.updateTerm(message.id, data.value);
              detectIsDraftTermsUpdated(true);
            }}
          />
        </div>
      </StyledTooltip>
    );
  }

  if (insertIntlJSXPartOptions) {
    return InsertIntlJSXPart({ ...insertIntlJSXPartOptions, isDevToolsEnabled, text });
  }

  return isDevToolsEnabled ? (
    <span data-testid={message.id || 'localeFragment'}>{reactMarkdownText}</span>
  ) : (
    reactMarkdownText
  );
};
